import { List } from 'immutable';
import { LocalizationService } from 'app/services/localization';
import { PREFERRED_LOCALE_KEY } from 'app/models/localStorage/constants';
import { getItem } from 'app/models/localStorage/selectors';
import { setItem, removeItem } from 'app/models/localStorage/actions';
import { LOCALE_KEY as SESSION_LOCALE } from 'app/models/sessionStorage/constants';
import { getItem as getSessionItem } from 'app/models/sessionStorage/selectors';
import {
  setItem as setSessionItem,
  removeItem as removeSessionItem,
} from 'app/models/sessionStorage/actions';
import { User } from 'app/constants/schemas';
import { getAuthUser } from 'app/models/auth/selectors';
import { refreshAuthUser } from 'app/models/auth/actions';
import { saveUser } from 'app/models/entities/remote';
import { setAttribute } from 'app/models/entities/actions';
import { debug } from 'app/services/logger';
import { getLanguageById } from 'app/constants/enums/Language';

export class PersistedLocalizationService extends LocalizationService {
  constructor(dispatch) {
    super();
    this.dispatch = dispatch;
  }

  /**
   * Persists locale into the localStorage and sessionStorage.
   *
   * @param {string}  locale
   * @param {boolean} persist Determines whether if the locale should get persisted to
   *                          localStorage and sessionStorage.
   * @override
   */
  setLocale(locale, persist = true) {
    // This is needed because super() calls setLocale(), before this.dispatch is set.
    if (this.dispatch && persist) {
      const stringifiableLocale = List.isList(locale) ? locale.toArray() : locale;
      this.dispatch(setItem(PREFERRED_LOCALE_KEY, stringifiableLocale));
      this.dispatch(setSessionItem(SESSION_LOCALE, stringifiableLocale));
    }

    super.setLocale(locale);
  }
}

/**
 * Initializes the localization service.
 *
 * @param {Object} options
 * @param {string | undefined} options.initLocale   This locale will be stored to the sessionStorage
 * @param {string} options.defaultLocale
 * @param {string} options.fallbackTimezone
 */
export const init = ({ initLocale, defaultLocale, fallbackTimezone } = {}) => (dispatch, getState) => {
  const persistedLocalizationService = new PersistedLocalizationService(dispatch);

  let storedLocale;
  let sessionLocale;

  try {
    storedLocale = getItem(getState(), PREFERRED_LOCALE_KEY);
  } catch (e) {
    // If there was an error parsing the JSON stored at the given localStorage key,
    // remove the data stored at that key.
    //
    // TODO: Should this behavior just be part of the localStorage service for all keys?
    dispatch(removeItem(PREFERRED_LOCALE_KEY));
  }

  try {
    sessionLocale = getSessionItem(getState(), SESSION_LOCALE);
  } catch (e) {
    dispatch(removeSessionItem(SESSION_LOCALE));
  }

  const preferredLocale = [
    { locale: initLocale, sessionPersist: true },
    { locale: sessionLocale },
    { locale: storedLocale },
    { locale: defaultLocale },
  ]
    .filter(({ locale }) => !!locale)
    .find(({ locale }) => {
      try {
        persistedLocalizationService.setLocale(locale, false);
        return true;
      } catch (e) {
        return false;
      }
    });

  if (preferredLocale === undefined) {
    debug('Unable to launch with a valid locale', {
      initLocale,
      sessionLocale,
      storedLocale,
      defaultLocale,
    });
    throw new Error('Unable to launch with a valid locale.');
  }

  if (preferredLocale.sessionPersist) {
    dispatch(setSessionItem(SESSION_LOCALE, preferredLocale.locale));
  }

  // Initialize localizationService time zone to its default value based on market
  persistedLocalizationService.setFallbackTimezone(fallbackTimezone);

  return persistedLocalizationService;
};

/**
 * Sets the timezone and locale information based on the user info.
 */
export const setTimezoneAndLocaleFromUserInfo = (localizationService) => (dispatch, getState) => {
  const {
    timezone: tz,
    language: lang,
  } = getAuthUser(getState());

  if (tz) {
    localizationService.setTimezone(tz.name);
  }

  // Update the locale based on the current user's language preference.
  if (lang) {
    localizationService.setLocale(lang.id);
  }
};

/**
 * If a user is logged in, update their language in the backend with the given locale.
 *
 * @param {String} locale
 */
export const updateUserLocale = (locale) => async (dispatch, getState) => {
  const user = getAuthUser(getState());
  if (user) {
    dispatch(setAttribute({ type: User, id: user.id, attribute: 'language', value: getLanguageById(locale) }));
    // If you save the user, you have to ensure that calling locales happens after
    await dispatch(saveUser(user.id));
    dispatch(refreshAuthUser());
  }
};

/**
 * Set the locale and update any logged in user's language preference.
 *
 * @param {String} locale
 */
export const setAndPersistUserLocale = (localizationService, locale) =>
  async (dispatch, getState) => {
    await dispatch(updateUserLocale(locale));
    localizationService.setLocale(locale);
  };
